/**
 * El velo: la capa que hace legible el titular sobre un vídeo que cambia
 * de brillo veinticuatro veces por segundo.
 *
 * ══════════════════════════════════════════════════════════════════════
 * LA MEDICIÓN. No cambies estos números sin rehacerla.
 *
 * Se analizaron los 250 fotogramas de `hero-loop.mp4` píxel a píxel. El
 * fondo NO es "oscuro en promedio y ya": la forma plateada tiene brillos
 * reventados que llegan a **blanco puro** —luminancia relativa 1.000— y el
 * percentil 99.9 de la banda donde caen los textos también es 1.000. Medir
 * contra la media habría dado un velo cómodo y un titular que desaparece
 * cada vez que la flor gira hacia la luz.
 *
 * Al peor píxel se le suman TODAS las capas decorativas a su valor máximo
 * —línea de retícula al 5 % de blanco, centro del resplandor al 22 % de
 * violeta, elemento flotante al 14 % de lila—. El fondo peor caso queda en
 * rgb(216, 213, 243), luminancia 0.686.
 *
 * Contra ese fondo, y con el velo de esta capa MÁS el panel del texto
 * (`ds-ink` al 70 %, en `ShopHeroCopy`):
 *
 *   escritorio  velo 40 % + panel 70 %  →  alfa efectiva 0.82
 *       blanco          10.4 : 1
 *       ds-mist          8.3 : 1
 *       ds-lilac         5.9 : 1   ← el rótulo de 11 px, el texto más pequeño
 *
 *   móvil       velo 60 % + panel 70 %  →  alfa efectiva 0.88
 *       blanco          12.3 : 1
 *       ds-mist          9.8 : 1
 *       ds-lilac         7.0 : 1
 *
 * El mínimo de la WCAG para texto pequeño es 4.5 : 1. El peor de estos
 * números lo supera en un 30 %, y eso ocurre en el fotograma más claro,
 * con el cursor encima y un halo debajo — las tres cosas a la vez.
 *
 * `ds-lavender` NO pasa en este fondo (3.4 : 1) y por eso no se usa para
 * texto en la portada.
 * ══════════════════════════════════════════════════════════════════════
 *
 * ── Por qué el velo es más denso en móvil ────────────────────────────
 * En escritorio el vídeo está apartado a la derecha y la columna del
 * texto es tinta limpia, así que un 40 % basta y sobra: el vídeo se sigue
 * viendo con fuerza donde no hay nada escrito. En un teléfono no hay dos
 * columnas: la forma del vídeo queda justo detrás del titular, y ahí el
 * velo tiene que trabajar.
 */
export function ShopHeroVeil() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      {/* 1 · El velo de base. Es el número de la medición. */}
      <div className="absolute inset-0 bg-ds-ink/60 lg:bg-ds-ink/40" />

      {/*
        2 · Solo en escritorio: la columna izquierda se va a tinta sólida.
        No es decoración, es el mismo problema resuelto por segunda vez —
        primero apartando el vídeo (la máscara de `ShopHeroBackdrop`),
        luego apagando lo que quede. Que dos defensas independientes tengan
        que fallar a la vez para que un titular se vuelva ilegible es
        deliberado.

        Las paradas están puestas a mano (30 % / 52 % / 78 %) y no
        repartidas solas. Con el reparto por defecto, la tinta seguía
        pesando casi un 45 % al 70 % del ancho y el vídeo se veía turbio
        de lado a lado. Al 52 % —el borde derecho de la columna de
        texto— sigue habiendo un 80 % de tinta; a partir del 78 % solo
        queda el velo de base y la forma del vídeo se ve como en la
        landing.
      */}
      <div className="absolute inset-0 hidden bg-gradient-to-r from-ds-ink from-30% via-ds-ink/80 via-52% to-transparent to-78% lg:block" />

      {/*
        3 · Bajo la barra superior. La barra es crema translúcida con
        desenfoque; sin esta banda, el brillo del vídeo se cuela por detrás
        y el logotipo pierde su fondo.
      */}
      <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-ds-ink/80 to-transparent" />

      {/*
        4 · El pie de la portada: da fondo al botón de bajar y cierra el
        borde contra la crema de la página, para que el corte entre la
        portada oscura y la rejilla clara sea una decisión y no un
        accidente.
      */}
      <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-ds-ink via-ds-ink/70 to-transparent" />
    </div>
  );
}
